import {
  useRef,
  useLayoutEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
interface Point {
  cx: number;
  cy: number;
}

export interface SVGConnectorHandle {
  setProgress: (value: number) => void;
}

interface SVGConnectorProps {
  fromRef: React.RefObject<HTMLElement | null>;
  toRef: React.RefObject<HTMLElement | null>;
  path: string;
  pathFrom: { x: number; y: number };
  pathTo: { x: number; y: number };
  containerRef: React.RefObject<HTMLElement | null>;
  color?: string;
  baseColor?: string;
  strokeWidth?: number;
  zIndex?: number;
}
export const SVGConnector = forwardRef<SVGConnectorHandle, SVGConnectorProps>(
  (
    {
      fromRef,
      toRef,
      path,
      pathFrom,
      pathTo,
      containerRef,
      color = "#6366f1",
      baseColor = "#d1d5db",
      strokeWidth = 2,
      zIndex = 10,
    },
    ref,
  ) => {
    const [transform, setTransform] = useState<string | null>(null);
    const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
    const [scaledStroke, setScaledStroke] = useState(strokeWidth);
    const pathRef = useRef<SVGPathElement>(null);
    const [totalLength, setTotalLength] = useState(0);
    const [progress, setProgress] = useState(0);
    useImperativeHandle(ref, () => ({ setProgress }));
    useLayoutEffect(() => {
      const measure = () => {
        const container = containerRef.current;
        const from = fromRef.current;
        const to = toRef.current;
        if (!container || !from || !to) return;
        const base = container.getBoundingClientRect();
        const center = (el: Element): Point => {
          const r = el.getBoundingClientRect();
          return {
            cx: r.left + r.width / 2 - base.left,
            cy: r.top + r.height / 2 - base.top,
          };
        };

        const p1 = center(from);
        const p2 = center(to);
        const tdx = pathTo.x - pathFrom.x;
        const tdy = pathTo.y - pathFrom.y;
        const adx = p2.cx - p1.cx;
        const ady = p2.cy - p1.cy;
        const scale = Math.hypot(adx, ady) / Math.hypot(tdx, tdy);
        const rotation = Math.atan2(ady, adx) - Math.atan2(tdy, tdx);
        const rotDeg = (rotation * 180) / Math.PI;
        const cos = Math.cos(rotation);
        const sin = Math.sin(rotation);
        const mappedX = scale * (pathFrom.x * cos - pathFrom.y * sin);
        const mappedY = scale * (pathFrom.x * sin + pathFrom.y * cos);
        const tx = p1.cx - mappedX;
        const ty = p1.cy - mappedY;
        setTransform(
          `translate(${tx}, ${ty}) rotate(${rotDeg}) scale(${scale})`,
        );
        setScaledStroke(strokeWidth / scale);
        setContainerSize({ w: base.width, h: base.height });
      };
      requestAnimationFrame(() => measure());
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }, [fromRef, toRef, containerRef, pathFrom, pathTo, strokeWidth]);
    useLayoutEffect(() => {
      if (pathRef.current) {
        setTotalLength(pathRef.current.getTotalLength());
      }
    }, [transform, path]);
    if (!transform) return null;
    const dashOffset = totalLength * (1 - progress);
    return (
      <svg
        style={{
          position: "absolute",
          inset: 0,
          width: containerSize.w,
          height: containerSize.h,
          overflow: "visible",
          pointerEvents: "none",
          zIndex: zIndex,
        }}
      >
        <g transform={transform}>
          <path
            d={path}
            fill="none"
            stroke={baseColor}
            strokeWidth={scaledStroke}
            strokeLinecap="round"
          />
          <path
            ref={pathRef}
            d={path}
            fill="none"
            stroke={color}
            strokeWidth={scaledStroke}
            strokeLinecap="round"
            strokeDasharray={totalLength || undefined}
            strokeDashoffset={totalLength ? dashOffset : undefined}
          />
        </g>
      </svg>
    );
  },
);

SVGConnector.displayName = "SVGConnector";
